/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { EMPTY_STRING } from "@/data/constants/marketing";
import { FILTER_ATTRIBUTES, NEAREST_STORE_MAX_ITEMS, PROFILE_NAME, RADIUS_UOM, NEAREST_STORE_LIST_RADIUS } from '@/data/constants/storeLocator';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocationIQAutoComplete } from '@/data/Content/LocationIQ';
import { useLocalization } from '@/data/Localization';
import { LatLng, StoreSearchAutoCompleteOption } from "@/data/types/Store";
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { debounce } from "lodash";
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { searchButtonSX } from '../styles/SideList/autoComplete';
import { findStoreAttributeValueByName } from "@/data/utils/StoreStock";
import { ContentContext } from "@/data/context/content";
import { useStoreStockByLocation } from "@/data/Content/StoreLocator";

export const StoreLocatorAutocomplete: FC = () => {

    const options = {
        radius: NEAREST_STORE_LIST_RADIUS,
        radiusUOM: RADIUS_UOM,
        maxItems: NEAREST_STORE_MAX_ITEMS,
        profileName: PROFILE_NAME,
        sortBy: '',
        filterAttributes: FILTER_ATTRIBUTES
    }
    const localization = useLocalization('StoreLocator');
    const [searchKey, setSearchKey] = useState(EMPTY_STRING);
    const [finalSearchKey, setFinalSearchKey] = useState(EMPTY_STRING);
    const { lqAutoCompleteOtions } = useLocationIQAutoComplete(finalSearchKey);
    const [latLon, setLatLon] = useState<LatLng>({} as LatLng);
    const { stores } = useStoreStockByLocation(latLon, options);
    const router = useNextRouter();
    const storeLocatorRoute = useLocalization('Routes').StoreLocator.route.t();
    const StoreLocatorNLS = useLocalization('StoreLocator');
    const {
        setMapCenter,
        setZoom
    } = useContext(ContentContext) as {
        setMapCenter?:(latLng:LatLng)=>void;
        setZoom?:(zoom:Number)=>void
    }

    useEffect(() => {
        if (searchKey.length > 2) {
            debouncedSeachInpChangeHandler(searchKey)
        } else {
            setFinalSearchKey(EMPTY_STRING);
        }
    }, [searchKey]);

    const debouncedSeachInpChangeHandler = useCallback(
        debounce((searchKey) => {
            setFinalSearchKey(searchKey);
        }, 400)
        , []);

    const handleInputChnage = useCallback((e: unknown, v: string | StoreSearchAutoCompleteOption | null) => {
        if (e === null) {
            setSearchKey("")
            setLatLon({} as LatLng);
            return false;
        }
        if (v && typeof v !== "string" && v.lat && v.lon) {
            const city = v.address.name;
            setSearchKey(v.display_name)
            setLatLon({
                lat: parseFloat(v.lat),
                lng: parseFloat(v.lon)
            });
            if(typeof setMapCenter === 'function'){
                setMapCenter({
                    lat: parseFloat(v.lat),
                    lng: parseFloat(v.lon)
                })
                if(typeof setZoom === 'function'){
                    setZoom(16);
                }
            }
        }
    },[])
    useEffect(() => {
        if(stores.length>0){
            let tempStores = [...stores];
            setLatLon({} as LatLng)
            let city:string = '';
            for(let x=0;x<(tempStores?.length??0);  x++){
                city = findStoreAttributeValueByName(tempStores[x].attributes,"seotokencity");
                if(city !== ''){
                    break;
                } 
            }
            if(city !== '') {
                 router.push({pathname: storeLocatorRoute + '/' + city})
            }
        }
    },[stores]);

    return (
        <Box >
            <Autocomplete
                id="storeLocatorSearchInput"
                fullWidth
                inputValue={searchKey}
                options={lqAutoCompleteOtions}
                disableClearable={true}
                autoHighlight
                selectOnFocus
                filterOptions={(options) => options}
                freeSolo
                forcePopupIcon={false}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.place_id}
                isOptionEqualToValue={(option, value) => option.place_id === value.place_id}
                renderOption={(props, option) => {
                    const boldString = (str: string, substr: string) =>
                        substr.length > 2 ? str.replace(new RegExp(substr, 'ig'), '<b style="text-transform:capitalize">' + substr + '</b>') : str;
                    return (
                        <Box component="li"  {...props} px={2} py={0.5}>
                            <Box>
                                <Typography dangerouslySetInnerHTML={{ __html: boldString(option.address.name, searchKey) }} />
                                <Typography sx={{ fontSize: 12 }}>{option.address.state},{option.address.country}</Typography>
                            </Box>
                        </Box>
                    )
                }}
                sx={{
                    "& .MuiAutocomplete-popupIndicator": { transform: "none" },
                }}
                renderInput={(params) => (
                    <Box sx={{ display: 'flex' }}>
                        <TextField
                            {...params}
                            label={localization.searchStore.t()}
                            placeholder={localization.searchStore.t()}
                            fullWidth
                            onChange={(event) => setSearchKey(event.target.value)}
                        />
                    </Box>
                )}
                onFocus={() => setSearchKey(EMPTY_STRING)}
                onChange={(e, v) => handleInputChnage(e, v)}
            />


        </Box>
    );
}
